/* eslint-disable */
import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { useMutation, useQuery } from 'react-fetching-library';
import { compare } from 'fast-json-patch';
import { makeStyles } from '@material-ui/core';
import ContentWrapper from 'components/Wrappers/ContentWrapper';
import RoundedButton from 'components/Shared/RoundedButton';
import showServerError from 'helpers/showError';
import UserForm from './widgets/UserForm';
import Loader from 'components/Shared/Loader';
import { ALL_ROUTES } from 'config/routes';
import useMetadataUpdate from 'hooks/useMetadataUpdate';
import { AppRouteContext } from 'contexts';
import BackButton from 'components/Shared/BackButton';
import getFullName from 'helpers/user';
import { useDispatch, useSelector } from 'react-redux';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
} from '../../../redux_store/reducer/reducers/notificationsReducer';
import { getTransitionWithVariable } from '../../../redux_store/reducer/reducers/i18nReducer';

const useStyles = makeStyles((theme) => ({
  contentContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
  },
  formCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'auto',
    margin: theme.spacing(3, 0),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2, 0),
      borderRadius: 12,
    },
    marginBottom: theme.spacing(10),
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
    width: '100%',
  },
  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
  },
  formContent: {
    width: '100%',
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  actionsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    padding: theme.spacing(3, 6),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 4),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2),
      gap: theme.spacing(1),
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
    [theme.breakpoints.down('sm')]: {
      minWidth: 100,
      padding: theme.spacing(1, 2),
      fontSize: 13,
    },
  },
  backButton: {
    marginRight: 'auto',
  },
}));

const UserEdit = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { en: { errors, notifications, btn, titles } } = useSelector((state) => state.i18n);
  const { setAppRoute } = useContext(AppRouteContext);
  const { accountUuid, userUuid, currentAccountUuid } = useParams();
  const [user, setUser] = useState();
  const [originUser, setOriginUser] = useState();
  const [isFailed, setIsFailed] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [backendErrors, setBackendErrors] = useState({});

  const { query } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${accountUuid || currentAccountUuid}/users/${userUuid}`,
    },
    false,
  ]);

  useEffect(() => {
    query().then(({ error, payload }) => {
      if (!error) {
        setOriginUser(payload);
        setUser(payload);
      } else {
        setAppRoute({
          path: ALL_ROUTES.notFound.path,
          clearBreadcrumbs: true,
          removeQueryString: true,
        });
      }
    });
  }, [query]);

  const userEditAction = (patches) => ({
    method: 'PATCH',
    endpoint: `/accounts/${accountUuid || currentAccountUuid}/users/${userUuid}`,
    body: patches,
  });

  const { loading, mutate } = useMetadataUpdate(useMutation, [userEditAction]);

  const editUser = async (values, { setSubmitting }) => {
    const patches = compare(originUser, values);
    if (patches.length) {
      setBackendErrors({});
      const { payload, error, status } = await mutate(patches);
      if (error) {
        setIsFailed(true);
        setTimeout(() => {
          setIsFailed(false);
        }, 2000);
        const options = payload && {
          correlationUUID: payload.correlationUUID,
          userUUID: user.uuid,
        };
        switch (status) {
          case 422: {
            showServerError(dispatch, errors.validationError);
            setBackendErrors(payload.validationErrors);
            break;
          }
          default: {
            showServerError(dispatch, '', options);
            break;
          }
        }
      } else if (payload) {
        setIsSuccess(true);
        setTimeout(() => {
          setIsSuccess(false);
        }, 2000);
        setOriginUser(payload);
        setUser(payload);
        dispatch(
          enqueueSnackbar({
            message: getTransitionWithVariable(notifications.userEdited, {
              name: getFullName(values),
            }),
            options: {
              variant: SNACKBAR_VARIANTS.success,
            },
          }),
        );
      }
      setSubmitting(false);
    }
  };

  return user ? (
    <Formik
      initialValues={user}
      validateOnBlur={false}
      enableReinitialize
      onSubmit={editUser}
    >
      {({ isValid, values, handleSubmit, isSubmitting, dirty }) => {
        return (
          <ContentWrapper
            titleText={titles.editUser}
            containerStyle={{
              overflow: 'visible',
              maxHeight: 'calc(100% - 35px)',
              boxSizing: 'border-box',
              paddingBottom: 0,
            }}
          >
            <div className={classes.contentContainer}>
              <div className={classes.formCard}>
                <UserForm
                  values={values}
                  initAllowedAccounts={originUser?.allowedAccounts ?? []}
                  backendErrors={backendErrors}
                  setBackendErrors={setBackendErrors}
                />
              </div>
              <div className={classes.actionsContainer}>
                <BackButton className={classes.backButton} />
                <RoundedButton
                  variant='contained'
                  color='primary'
                  disabled={!isValid || isSubmitting || !dirty}
                  onClick={handleSubmit}
                  isLoading={loading}
                  isSuccess={isSuccess}
                  isFailed={isFailed}
                  className={classes.actionButton}
                >
                  {btn.save}
                </RoundedButton>
              </div>
            </div>
          </ContentWrapper>
        );
      }}
    </Formik>
  ) : (
    <ContentWrapper>
      <Loader fullHeight />
    </ContentWrapper>
  );
};

export default UserEdit;
